/**
 * DataSection (ADR-0010 P4.4b) — the Data tab of the table designer:
 * a saved-Views bar + a column-visibility menu wrapped around the
 * {@link DataGrid}.
 *
 * A **view** persists which columns are visible and in what order (its
 * `config.columns`). Selecting a view applies that selection; "Save
 * view" captures the current selection as a named view. The grid itself
 * is unchanged — DataSection just feeds it a filtered/ordered `columns`
 * subset. Sort/filter live in the view config for forward-compat but
 * have no builder UI yet (a later slice).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Columns3, Plus, Table2, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { type GridSchemaMenu } from "@/components/apps/DataGrid";
import { DataGridTable } from "@/components/apps/DataGridTable";
import { appsApi, type AppEnv, type ColumnOut, type ViewConfig, type ViewOut } from "@/lib/api-apps";
import { cn } from "@/lib/cn";


/** Resolve a view's saved column selection to an ordered, still-existing
 *  column list. Unknown ids (dropped columns) are skipped; an empty/absent
 *  selection means "all columns in their natural order". Pure — unit-tested. */
export function applyViewColumns(columns: ColumnOut[], config: ViewConfig | undefined): ColumnOut[] {
  const ids = config?.columns;
  if (!ids || ids.length === 0) return columns;
  const byId = new Map(columns.map((c) => [c.id, c]));
  return ids.map((id) => byId.get(id)).filter((c): c is ColumnOut => Boolean(c));
}


export function DataSection({
  appId,
  tableId,
  columns,
  env = "dev",
  editable,
}: {
  appId: string;
  tableId: string;
  columns: ColumnOut[];
  /** ADR-0010 P5.5 — data environment to browse (forwarded to DataGrid). */
  env?: AppEnv;
  /** Airtable-style schema editing (admin table designer). When provided, the
   *  grid headers become field menus + a toolbar "add field" appears. `onHide`
   *  is supplied here (it's the view-visibility toggle this component owns);
   *  rename/drop/addField come from the page. Absent → pure data grid. */
  editable?: Pick<GridSchemaMenu, "onRename" | "onDrop" | "onAddField">;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [selectedViewId, setSelectedViewId] = React.useState<string | null>(null);
  // null = all columns; otherwise an ordered list of visible column ids.
  const [visibleIds, setVisibleIds] = React.useState<string[] | null>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const viewsQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId, "views"],
    queryFn: () => appsApi.listViews(appId, tableId),
  });
  const views = viewsQ.data ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "views"] });

  const createView = useMutation({
    mutationFn: (cfg: { name: string; config: ViewConfig }) =>
      appsApi.createView(appId, tableId, { name: cfg.name, config: cfg.config }),
    onSuccess: (v) => {
      invalidate();
      setSelectedViewId(v.id);
    },
  });

  const deleteView = useMutation({
    mutationFn: (viewId: string) => appsApi.deleteView(appId, tableId, viewId),
    onSuccess: () => {
      invalidate();
      setSelectedViewId(null);
      setVisibleIds(null);
    },
  });

  const selectView = (view: ViewOut | null) => {
    setSelectedViewId(view?.id ?? null);
    setVisibleIds(view ? applyViewColumns(columns, view.config).map((c) => c.id) : null);
    setMenuOpen(false);
  };

  const toggleColumn = (id: string) => {
    // Editing visibility makes the selection "custom" (no saved view).
    setSelectedViewId(null);
    setVisibleIds((prev) => {
      const current = prev ?? columns.map((c) => c.id);
      return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    });
  };

  const displayColumns = applyViewColumns(columns, visibleIds ? { columns: visibleIds } : undefined);
  const effectiveIds = new Set(displayColumns.map((c) => c.id));

  const saveView = () => {
    const name = prompt(t("apps.viewNamePrompt", "Name this view"));
    if (!name?.trim()) return;
    createView.mutate({
      name: name.trim(),
      config: { columns: displayColumns.map((c) => c.id) },
    });
  };

  // View pills (left of the grid's unified toolbar).
  const viewPills = (
    <>
      <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <ViewPill active={selectedViewId === null} onClick={() => selectView(null)}>
        {t("apps.allColumns", "All")}
      </ViewPill>
      {views.map((v) => (
        <ViewPill key={v.id} active={selectedViewId === v.id} onClick={() => selectView(v)}>
          {v.name}
        </ViewPill>
      ))}
    </>
  );

  // View actions (right cluster, before the grid's own density / +field / +row).
  const tbBtn =
    "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted-foreground transition hover:bg-elevated hover:text-foreground";
  const viewActions = (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          title={t("apps.columns", "Columns")}
          className={tbBtn}
        >
          <Columns3 className="h-4 w-4" strokeWidth={1.5} />
          <span className="hidden md:inline">{t("apps.columns", "Columns")}</span>
        </button>
        {menuOpen && (
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-border bg-elevated p-2 shadow-lg">
            <p className="px-1 pb-1 text-[11px] font-medium text-muted-foreground">
              {t("apps.visibleColumns", "Visible columns")}
            </p>
            <div className="max-h-60 space-y-0.5 overflow-auto">
              {columns.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={effectiveIds.has(c.id)}
                    onChange={() => toggleColumn(c.id)}
                  />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={saveView}
        disabled={createView.isPending}
        title={t("apps.saveViewHint", "Save the current column selection as a view")}
        className={`${tbBtn} disabled:opacity-50`}
      >
        <Plus className="h-4 w-4" strokeWidth={1.5} />
        <span className="hidden md:inline">{t("apps.saveView", "Save view")}</span>
      </button>

      {selectedViewId && (
        <button
          type="button"
          aria-label={t("apps.deleteView", "Delete view")}
          title={t("apps.deleteView", "Delete view")}
          onClick={() => {
            if (confirm(t("apps.confirmDeleteView", "Delete this view?"))) {
              deleteView.mutate(selectedViewId);
            }
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-elevated hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      )}
    </>
  );

  return (
    <DataGridTable
      appId={appId}
      tableId={tableId}
      columns={displayColumns}
      env={env}
      toolbarLeft={viewPills}
      toolbarRight={viewActions}
      schema={
        editable
          ? {
              onRename: editable.onRename,
              onDrop: editable.onDrop,
              onAddField: editable.onAddField,
              // Hiding is THIS component's job (view-visibility), not the page's.
              onHide: toggleColumn,
            }
          : undefined
      }
    />
  );
}


function ViewPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md px-2 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-elevated text-foreground"
          : "text-muted-foreground hover:bg-elevated/60 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
